import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PageHeader from '@/components/dashboard/PageHeader';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, ChevronLeft, ChevronRight, Plus, RefreshCw, CalendarDays } from 'lucide-react';
import CalendarMonth from '@/components/schedule/CalendarMonth';
import CalendarWeek from '@/components/schedule/CalendarWeek';
import AssignmentDialog from '@/components/schedule/AssignmentDialog';
import {
  fetchScheduleRange,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  formatMonthLabel,
  formatDayShort,
  isoDate,
} from '@/lib/scheduleData';
import { fetchActiveJobsForPicker } from '@/lib/timeEntriesData';

export default function SchedulePage() {
  const { t } = useTranslation();
  const [view, setView] = useState('month');
  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [assignments, setAssignments] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogDate, setDialogDate] = useState(new Date());
  const [editing, setEditing] = useState(null);

  const range = useMemo(() => {
    if (view === 'month') {
      return { from: startOfMonth(cursor), to: endOfMonth(cursor) };
    }
    return { from: startOfWeek(cursor), to: endOfWeek(cursor) };
  }, [view, cursor]);

  const load = useCallback(
    async ({ silent = false } = {}) => {
      if (silent) setRefreshing(true);
      else setLoading(true);
      setError(null);
      try {
        const [scheduleData, jobsData] = await Promise.all([
          fetchScheduleRange({ from: range.from, to: range.to }),
          fetchActiveJobsForPicker(),
        ]);
        setAssignments(scheduleData);
        setJobs(jobsData);
      } catch (e) {
        setError(e?.message || t('schedule.loadError'));
      } finally {
        if (silent) setRefreshing(false);
        else setLoading(false);
      }
    },
    [range.from, range.to, t]
  );

  useEffect(() => {
    load();
  }, [load]);

  const goToday = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    setCursor(d);
  };

  const stepBack = () => {
    const d = new Date(cursor);
    if (view === 'month') d.setMonth(d.getMonth() - 1);
    else d.setDate(d.getDate() - 7);
    setCursor(d);
  };

  const stepForward = () => {
    const d = new Date(cursor);
    if (view === 'month') d.setMonth(d.getMonth() + 1);
    else d.setDate(d.getDate() + 7);
    setCursor(d);
  };

  const openAddForDate = (date) => {
    setDialogDate(date);
    setEditing(null);
    setDialogOpen(true);
  };

  const openEditAssignment = (a) => {
    setDialogDate(new Date(a.date));
    setEditing(a);
    setDialogOpen(true);
  };

  const handleSaved = (saved) => {
    setAssignments((cur) => {
      const idx = cur.findIndex((a) => a.id === saved.id);
      if (idx >= 0) {
        const next = cur.slice();
        next[idx] = saved;
        return next;
      }
      return [...cur, saved];
    });
  };

  const handleDeleted = (removed) => {
    setAssignments((cur) => cur.filter((a) => a.id !== removed.id));
  };

  const cursorLabel =
    view === 'month'
      ? formatMonthLabel(cursor)
      : t('schedule.weekOf', { date: formatDayShort(startOfWeek(cursor)) });

  return (
    <div data-testid="schedule-page">
      <PageHeader
        eyebrow={t('schedule.eyebrow')}
        title={t('schedule.title')}
        subtitle={t('schedule.subtitle')}
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => load({ silent: true })}
              disabled={loading || refreshing}
              className="border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 uppercase tracking-[0.16em] text-xs"
              data-testid="schedule-refresh-button"
            >
              <RefreshCw
                size={14}
                className={`mr-2 ${refreshing ? 'animate-spin' : ''}`}
              />
              {t('schedule.refresh')}
            </Button>
            <Button
              size="sm"
              onClick={() => openAddForDate(new Date())}
              className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.16em] text-xs"
              data-testid="schedule-add-button"
            >
              <Plus size={14} className="mr-2" />
              {t('schedule.addAssignment')}
            </Button>
          </div>
        }
      />

      {error ? (
        <Alert
          className="mb-5 border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 text-foreground"
          data-testid="schedule-error"
        >
          <AlertCircle className="h-4 w-4 text-[hsl(var(--destructive))]" />
          <AlertDescription className="text-sm">{error}</AlertDescription>
        </Alert>
      ) : null}

      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="icon"
            variant="outline"
            onClick={stepBack}
            className="border-border h-9 w-9"
            data-testid="schedule-prev"
            aria-label={t('schedule.prevAria')}
          >
            <ChevronLeft size={14} />
          </Button>
          <div
            className="min-w-[150px] sm:min-w-[180px] flex-1 sm:flex-none text-center text-sm font-semibold text-foreground"
            data-testid="schedule-cursor-label"
          >
            {cursorLabel}
          </div>
          <Button
            size="icon"
            variant="outline"
            onClick={stepForward}
            className="border-border h-9 w-9"
            data-testid="schedule-next"
            aria-label={t('schedule.nextAria')}
          >
            <ChevronRight size={14} />
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={goToday}
            className="sm:ml-2 border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 uppercase tracking-[0.16em] text-xs"
            data-testid="schedule-today-button"
          >
            <CalendarDays size={14} className="mr-1.5" />
            {t('schedule.today')}
          </Button>
        </div>
        <Tabs value={view} onValueChange={setView}>
          <TabsList
            className="bg-[hsl(var(--muted))] border border-border h-9 p-1"
            data-testid="schedule-view-tabs"
          >
            <TabsTrigger
              value="month"
              className="data-[state=active]:bg-[hsl(var(--card))] data-[state=active]:text-[hsl(var(--primary))] data-[state=active]:border data-[state=active]:border-[hsl(var(--primary))]/30 uppercase tracking-[0.16em] text-xs"
              data-testid="schedule-view-month"
            >
              {t('schedule.viewMonth')}
            </TabsTrigger>
            <TabsTrigger
              value="week"
              className="data-[state=active]:bg-[hsl(var(--card))] data-[state=active]:text-[hsl(var(--primary))] data-[state=active]:border data-[state=active]:border-[hsl(var(--primary))]/30 uppercase tracking-[0.16em] text-xs"
              data-testid="schedule-view-week"
            >
              {t('schedule.viewWeek')}
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {view === 'month' ? (
        <CalendarMonth
          cursor={cursor}
          assignments={assignments}
          jobs={jobs}
          onPickDate={openAddForDate}
          onPickAssignment={openEditAssignment}
        />
      ) : (
        <CalendarWeek
          cursor={cursor}
          assignments={assignments}
          jobs={jobs}
          onPickDate={openAddForDate}
          onPickAssignment={openEditAssignment}
        />
      )}

      <AssignmentDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        date={dialogDate}
        jobs={jobs}
        assignment={editing}
        onSaved={handleSaved}
        onDeleted={handleDeleted}
      />

      {/* Hidden helper to expose current ISO date for testing dialogs */}
      <div className="sr-only" data-testid="schedule-current-iso">
        {isoDate(cursor)}
      </div>
    </div>
  );
}
